import type { EditorialChecklistProgress } from "@/lib/editorial-checklist-progress";
import { countEditorialChecklistProgress } from "@/lib/editorial-checklist-progress";
import type { Enums, Tables } from "@/types/database.types";
import {
  appendLibrarySortParams,
  getNextLibrarySort,
  parseLibrarySortDirection,
  type LibrarySortDirection,
} from "@/core/services/types/library-sort.types";
import {
  DEFAULT_LIBRARY_PAGE_SIZE,
  appendLibraryPageSizeParam,
  parseLibraryPageSize,
  type LibraryPageSize,
} from "@/core/services/types/library-pagination.types";

export const CONTENT_STATUSES = [
  "SIGNAL",
  "DRAFT",
  "PENDING_REVIEW",
  "SCHEDULED",
  "PUBLISHED",
  "ARCHIVED",
] as const satisfies readonly Enums<"content_status">[];

export type ContentStatus = (typeof CONTENT_STATUSES)[number];

export const ARTICLE_LIBRARY_TABS = [
  "ALL",
  "DRAFT",
  "PENDING_REVIEW",
  "SCHEDULED",
  "PUBLISHED",
  "ARCHIVED",
] as const;

export type ArticleLibraryTab = (typeof ARTICLE_LIBRARY_TABS)[number];

export type ArticleSummary = Pick<
  Tables<"articles">,
  "id" | "title" | "sub_title" | "cover_image_url" | "published_at"
>;

export type Article = ArticleSummary &
  Pick<Tables<"articles">, "content_rich_text">;

export type ArticleEditor = Pick<
  Tables<"articles">,
  | "id"
  | "title"
  | "sub_title"
  | "cover_image_url"
  | "content_rich_text"
  | "status"
  | "published_at"
  | "assigned_to"
  | "updated_at"
  | "internal_notes"
>;

export type ArticleAssigneeOption = Pick<
  Tables<"team_members">,
  "id" | "first_name" | "last_name" | "is_active"
>;

export const ARTICLE_EDIT_STATUS_VALUES = [
  "SIGNAL",
  "DRAFT",
  "PENDING_REVIEW",
  "SCHEDULED",
  "PUBLISHED",
  "ARCHIVED",
] as const;

export type ArticleEditStatusValue = (typeof ARTICLE_EDIT_STATUS_VALUES)[number];

export type ArticleStatusActionVariant = "primary" | "secondary" | "danger";

export type ArticleStatusActionDisabledHintKey = "publishChecklistIncomplete";

export type ArticleStatusAction = {
  target?: ArticleEditStatusValue;
  labelKey:
    | "submitReview"
    | "publish"
    | "schedule"
    | "publishNow"
    | "revertDraft"
    | "promoteToDraft"
    | "cancelSchedule"
    | "archive"
    | "delete";
  variant: ArticleStatusActionVariant;
  disabled?: boolean;
  disabledHintKey?: ArticleStatusActionDisabledHintKey;
};

export function isArticlePublishStatus(
  status: ArticleEditStatusValue,
): boolean {
  return status === "PUBLISHED";
}

export function isArticleScheduleStatus(
  status: ArticleEditStatusValue,
): boolean {
  return status === "SCHEDULED";
}

export function isArticlePrivilegedPublishAction(
  action: ArticleStatusAction,
): boolean {
  if (action.labelKey === "schedule" || action.labelKey === "publishNow") {
    return true;
  }

  return Boolean(
    action.target &&
      (isArticlePublishStatus(action.target) ||
        isArticleScheduleStatus(action.target)),
  );
}

const EDITORIAL_DELETE_ACTION: ArticleStatusAction = {
  labelKey: "delete",
  variant: "danger",
};

export function isDeletableEditorialStatus(status: ContentStatus): boolean {
  return (
    status === "SIGNAL" ||
    status === "DRAFT" ||
    status === "PENDING_REVIEW"
  );
}

export function getArticleStatusActions(
  currentStatus: ArticleEditStatusValue,
  options?: {
    canPublish?: boolean;
    canDelete?: boolean;
    publishDisabled?: boolean;
  },
): ArticleStatusAction[] {
  let actions = getArticleStatusActionsForStatus(currentStatus);

  if (options?.canDelete === false) {
    actions = actions.filter((action) => action.labelKey !== "delete");
  }

  if (options?.canPublish === false) {
    return actions.filter((action) => !isArticlePrivilegedPublishAction(action));
  }

  if (options?.publishDisabled) {
    actions = actions.map((action) =>
      isArticlePrivilegedPublishAction(action)
        ? {
            ...action,
            disabled: true,
            disabledHintKey: "publishChecklistIncomplete",
          }
        : action,
    );
  }

  return actions;
}

function getArticleStatusActionsForStatus(
  currentStatus: ArticleEditStatusValue,
): ArticleStatusAction[] {
  switch (currentStatus) {
    case "SIGNAL":
      return [
        { target: "DRAFT", labelKey: "promoteToDraft", variant: "primary" },
        EDITORIAL_DELETE_ACTION,
      ];
    case "DRAFT":
      return [
        {
          target: "PENDING_REVIEW",
          labelKey: "submitReview",
          variant: "secondary",
        },
        { target: "PUBLISHED", labelKey: "publish", variant: "primary" },
        EDITORIAL_DELETE_ACTION,
      ];
    case "PENDING_REVIEW":
      return [
        { target: "DRAFT", labelKey: "revertDraft", variant: "secondary" },
        { target: "PUBLISHED", labelKey: "publish", variant: "primary" },
        EDITORIAL_DELETE_ACTION,
      ];
    case "SCHEDULED":
      return [
        { target: "PUBLISHED", labelKey: "publishNow", variant: "primary" },
        { target: "DRAFT", labelKey: "cancelSchedule", variant: "secondary" },
      ];
    case "PUBLISHED":
      return [
        { target: "ARCHIVED", labelKey: "archive", variant: "danger" },
      ];
    case "ARCHIVED":
      return [
        { target: "DRAFT", labelKey: "revertDraft", variant: "secondary" },
      ];
  }
}

export type UpdateArticleInput = {
  title: string;
  sub_title: string | null;
  content_rich_text: string | null;
  editStatus: ArticleEditStatusValue;
};

export type UpdateArticleContentInput = Pick<
  UpdateArticleInput,
  "title" | "sub_title" | "content_rich_text"
>;

export type UpdateArticleStatusInput = {
  editStatus: ArticleEditStatusValue;
  schedulePublishAt?: string | null;
  publishImmediately?: boolean;
};

export type UpdateArticleAssignmentInput = {
  assignedTo: string | null;
};

export type UpdateArticleInternalNotesInput = {
  internalNotes: string | null;
};

export type UpdateArticleCoverImageInput = {
  coverImageUrl: string | null;
};

export type UpdateArticleState =
  | { success: true; data: ArticleEditor }
  | { success: false; error?: string; fieldErrors?: Record<string, string> };

export type UploadArticleCoverImageResult =
  | { success: true; data: ArticleEditor }
  | { success: false; error: string };

export function resolveArticleEditStatus(article: {
  status: ContentStatus;
  published_at: string | null;
}): ArticleEditStatusValue {
  if (article.status === "PUBLISHED") return "PUBLISHED";
  if (article.status === "ARCHIVED") return "ARCHIVED";
  if (article.status === "SCHEDULED") return "SCHEDULED";
  if (article.status === "PENDING_REVIEW") return "PENDING_REVIEW";
  if (article.status === "SIGNAL") return "SIGNAL";
  return "DRAFT";
}

export type EditStatusDbFieldsOptions = {
  schedulePublishAt?: string | null;
  publishImmediately?: boolean;
};

export function editStatusToDbFields(
  editStatus: ArticleEditStatusValue,
  currentPublishedAt: string | null,
  options: EditStatusDbFieldsOptions = {},
): Pick<ArticleEditor, "status" | "published_at"> {
  const now = new Date().toISOString();

  switch (editStatus) {
    case "SIGNAL":
      return {
        status: "SIGNAL",
        published_at: null,
      };
    case "DRAFT":
      return {
        status: "DRAFT",
        published_at: null,
      };
    case "PENDING_REVIEW":
      return {
        status: "PENDING_REVIEW",
        published_at: null,
      };
    case "SCHEDULED":
      return {
        status: "SCHEDULED",
        published_at: options.schedulePublishAt ?? null,
      };
    case "PUBLISHED":
      return {
        status: "PUBLISHED",
        published_at: options.publishImmediately ? now : (currentPublishedAt ?? now),
      };
    case "ARCHIVED":
      return {
        status: "ARCHIVED",
        published_at: currentPublishedAt,
      };
  }
}

export function toDatetimeLocalValue(iso: string | null): string {
  if (!iso) return "";

  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";

  const offsetMs = date.getTimezoneOffset() * 60_000;
  return new Date(date.getTime() - offsetMs).toISOString().slice(0, 16);
}

export function toDateInputValue(iso: string | null): string {
  if (!iso) return "";

  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";

  const offsetMs = date.getTimezoneOffset() * 60_000;
  return new Date(date.getTime() - offsetMs).toISOString().slice(0, 10);
}

export function parseDateInputValue(value: string): string | null {
  const trimmed = value.trim();
  if (!trimmed) return null;
  if (!/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) return null;

  const date = new Date(`${trimmed}T00:00:00`);
  if (Number.isNaN(date.getTime())) return null;

  return date.toISOString();
}

export function parseDatetimeLocalValue(value: string): string | null {
  const trimmed = value.trim();
  if (!trimmed) return null;

  const date = new Date(trimmed);
  if (Number.isNaN(date.getTime())) return null;

  return date.toISOString();
}

/** Minimum schedule date for daily cron: tomorrow (local calendar day). */
export function getMinimumScheduleDateInputValue(): string {
  const tomorrow = new Date();
  tomorrow.setHours(0, 0, 0, 0);
  tomorrow.setDate(tomorrow.getDate() + 1);

  const offsetMs = tomorrow.getTimezoneOffset() * 60_000;
  return new Date(tomorrow.getTime() - offsetMs).toISOString().slice(0, 10);
}

export function isValidSchedulePublishDate(iso: string | null): boolean {
  if (!iso) return false;

  const scheduled = new Date(iso);
  if (Number.isNaN(scheduled.getTime())) return false;

  const minimumIso = parseDateInputValue(getMinimumScheduleDateInputValue());
  if (!minimumIso) return false;

  return scheduled.getTime() >= new Date(minimumIso).getTime();
}

export function getArticleSharePath(id: string): string {
  return `/articles/redaction/${id}`;
}

export function formatTeamMemberName(member: {
  first_name: string;
  last_name: string;
}): string {
  return `${member.first_name} ${member.last_name}`.trim();
}

export function getAssignableTeamMembers(
  members: ArticleAssigneeOption[],
  assignedTo: string | null,
): ArticleAssigneeOption[] {
  return members.filter(
    (member) => member.is_active || member.id === assignedTo,
  );
}

export type ArticleAdminListAssignee = Pick<
  Tables<"team_members">,
  "id" | "first_name" | "last_name"
>;

export type ArticleAdminListItem = Pick<
  Tables<"articles">,
  | "id"
  | "title"
  | "published_at"
  | "created_at"
  | "status"
  | "assigned_to"
  | "cover_image_url"
  | "sub_title"
  | "content_rich_text"
> & {
  assignee: ArticleAdminListAssignee | null;
};

export type ArticleRedactionListItem = ArticleAdminListItem &
  Pick<Tables<"articles">, "cover_image_url" | "sub_title" | "content_rich_text"> & {
    suggestedPlacesCount: number;
    suggestedEventsCount: number;
  };

export type ArticleListPage = {
  items: ArticleAdminListItem[];
  total: number;
  page: number;
  pageSize: LibraryPageSize;
};

export type ArticleStatusCounts = Record<ContentStatus, number>;

export type ArticleLibraryTabCounts = Record<ArticleLibraryTab, number>;

export type ArticleLibraryKpis = {
  total: number;
  publishedThisMonth: number;
  pendingReview: number;
};

export const ARTICLE_LIBRARY_PAGE_SIZE = DEFAULT_LIBRARY_PAGE_SIZE;

export { parseLibraryPageSize as parseArticlePageSize };
export const ARTICLE_SEARCH_MAX_LENGTH = 100;

export function parseContentStatus(value: string | undefined): ContentStatus {
  if (value && CONTENT_STATUSES.includes(value as ContentStatus)) {
    return value as ContentStatus;
  }
  return "DRAFT";
}

export function parseArticleLibraryTab(
  value: string | undefined,
): ArticleLibraryTab {
  if (value && ARTICLE_LIBRARY_TABS.includes(value as ArticleLibraryTab)) {
    return value as ArticleLibraryTab;
  }
  return "ALL";
}

export function parseArticlePage(value: string | undefined): number {
  const parsed = Number(value);
  if (!Number.isInteger(parsed) || parsed < 1) return 1;
  return parsed;
}

export function parseArticleSearchQuery(value: string | undefined): string {
  if (!value) return "";
  return value.trim().slice(0, ARTICLE_SEARCH_MAX_LENGTH);
}

export const ARTICLE_LIBRARY_SORT_COLUMNS = [
  "title",
  "assignment",
  "created_at",
  "published_at",
  "status",
] as const;

export type ArticleLibrarySortColumn =
  (typeof ARTICLE_LIBRARY_SORT_COLUMNS)[number];

export type ArticleLibrarySort = {
  column: ArticleLibrarySortColumn;
  direction: LibrarySortDirection;
};

export type ArticleLibrarySortState = ArticleLibrarySort | null;

export function parseArticleLibrarySort(
  column: string | undefined,
  direction: string | undefined,
): ArticleLibrarySortState {
  if (
    !column ||
    !ARTICLE_LIBRARY_SORT_COLUMNS.includes(column as ArticleLibrarySortColumn)
  ) {
    return null;
  }

  return {
    column: column as ArticleLibrarySortColumn,
    direction: parseLibrarySortDirection(direction),
  };
}

export function buildArticleLibraryHref({
  tab,
  page = 1,
  pageSize = DEFAULT_LIBRARY_PAGE_SIZE,
  searchQuery = "",
  sort = null,
}: {
  tab: ArticleLibraryTab;
  page?: number;
  pageSize?: LibraryPageSize;
  searchQuery?: string;
  sort?: ArticleLibrarySortState;
}): string {
  const params = new URLSearchParams({
    status: tab,
    page: String(page),
  });

  if (searchQuery) {
    params.set("q", searchQuery);
  }

  appendLibrarySortParams(params, sort);
  appendLibraryPageSizeParam(params, pageSize);

  return `/articles/library?${params.toString()}`;
}

export function buildArticleLibrarySortHref({
  tab,
  searchQuery = "",
  pageSize = DEFAULT_LIBRARY_PAGE_SIZE,
  sort,
  nextColumn,
}: {
  tab: ArticleLibraryTab;
  searchQuery?: string;
  pageSize?: LibraryPageSize;
  sort: ArticleLibrarySortState;
  nextColumn: ArticleLibrarySortColumn;
}): string {
  return buildArticleLibraryHref({
    tab,
    page: 1,
    pageSize,
    searchQuery,
    sort: getNextLibrarySort(sort, nextColumn),
  });
}

export const ARTICLE_SUGGESTION_SEARCH_MIN_LENGTH = 2;
export const ARTICLE_SUGGESTION_SEARCH_LIMIT = 8;

export type ArticleSuggestedPlace = {
  placeId: string;
  position: number | null;
  name: string;
  city: string | null;
  status: ContentStatus;
};

export type ArticleSuggestedEvent = {
  eventId: string;
  position: number | null;
  title: string;
  startDate: string;
  endDate: string;
  status: ContentStatus;
  eventCategory: Tables<"events">["event_category"];
};

export type ArticleSuggestionSearchPlace = Pick<
  Tables<"places">,
  "id" | "name" | "city" | "status"
>;

export type ArticleSuggestionSearchEvent = Pick<
  Tables<"events">,
  "id" | "title" | "start_date" | "end_date" | "status" | "event_category"
>;

export type ArticleSuggestions = {
  places: ArticleSuggestedPlace[];
  events: ArticleSuggestedEvent[];
};

export type ArticleEditorialChecklistState = {
  hasCoverImage: boolean;
  hasTitle: boolean;
  hasSubTitle: boolean;
  hasContent: boolean;
};

type ArticleEditorialChecklistFields = Pick<
  ArticleEditor,
  "cover_image_url" | "title" | "sub_title" | "content_rich_text"
>;

export function getArticleEditorialChecklist(
  article: ArticleEditorialChecklistFields,
): ArticleEditorialChecklistState {
  const title = article.title?.trim() ?? "";
  const hasTitle = Boolean(title) && title !== ARTICLE_CREATE_DEFAULT_TITLE;

  return {
    hasCoverImage: Boolean(article.cover_image_url?.trim()),
    hasTitle,
    hasSubTitle: Boolean(article.sub_title?.trim()),
    hasContent: !isArticleRichTextEmpty(article.content_rich_text),
  };
}

export function getArticleRedactionChecklist(
  article: Pick<
    ArticleRedactionListItem,
    "cover_image_url" | "title" | "sub_title" | "content_rich_text"
  >,
): ArticleEditorialChecklistState {
  return getArticleEditorialChecklist(article);
}

export function isArticleEditorialChecklistComplete(
  article: ArticleEditorialChecklistFields,
): boolean {
  const checklist = getArticleEditorialChecklist(article);
  return (
    checklist.hasCoverImage &&
    checklist.hasTitle &&
    checklist.hasSubTitle &&
    checklist.hasContent
  );
}

export function getArticleEditorialChecklistProgress(
  article: ArticleEditorialChecklistFields,
): EditorialChecklistProgress {
  const checklist = getArticleEditorialChecklist(article);
  return countEditorialChecklistProgress([
    checklist.hasCoverImage,
    checklist.hasTitle,
    checklist.hasSubTitle,
    checklist.hasContent,
  ]);
}

export type ArticleSuggestionMutationResult =
  | { success: true; data: ArticleSuggestions }
  | { success: false; error: string };

export type ArticleSuggestionSearchPlacesResult =
  | { success: true; places: ArticleSuggestionSearchPlace[] }
  | { success: false; error: string };

export type ArticleSuggestionSearchEventsResult =
  | { success: true; events: ArticleSuggestionSearchEvent[] }
  | { success: false; error: string };

export const ARTICLE_CREATE_DEFAULT_TITLE = "Nouvel article";

export type CreateContentInitialStatus = Extract<ContentStatus, "SIGNAL" | "DRAFT">;

export type CreateArticleInput = {
  title?: string;
  status?: CreateContentInitialStatus;
  assignedTo?: string | null;
};

export type CreateArticleResult =
  | { success: true; data: ArticleEditor }
  | { success: false; error: string };

export type CancelArticleCreationResult =
  | { success: true }
  | { success: false; error: "not_found" | "not_cancellable" | "delete_failed" };

export type DeleteSignalContentResult =
  | { success: true }
  | { success: false; error: "not_found" | "not_deletable" | "delete_failed" };

export function isArticleRichTextEmpty(value: string | null | undefined): boolean {
  if (!value?.trim()) return true;
  const trimmed = value.trim();
  return trimmed === "" || trimmed === "<p></p>";
}

export function getArticleContentPublishFieldErrors(
  content: Pick<ArticleEditor, "title" | "sub_title" | "content_rich_text">,
  messages: {
    titleRequired: string;
    subTitleRequired: string;
    contentRequired: string;
  },
): Record<string, string> {
  const fieldErrors: Record<string, string> = {};
  const title = content.title?.trim() ?? "";

  if (!title || title === ARTICLE_CREATE_DEFAULT_TITLE) {
    fieldErrors.title = messages.titleRequired;
  }

  if (!content.sub_title?.trim()) {
    fieldErrors.sub_title = messages.subTitleRequired;
  }

  if (isArticleRichTextEmpty(content.content_rich_text)) {
    fieldErrors.content_rich_text = messages.contentRequired;
  }

  return fieldErrors;
}

export function isCancellableArticleDraft(article: ArticleEditor): boolean {
  return (
    article.status === "DRAFT" &&
    article.title.trim() === ARTICLE_CREATE_DEFAULT_TITLE &&
    !article.sub_title?.trim() &&
    !article.cover_image_url &&
    !article.assigned_to &&
    !article.published_at &&
    isArticleRichTextEmpty(article.content_rich_text)
  );
}

export type ArticleTaxonomy = {
  artFormIds: string[];
  artisticPeriodIds: string[];
  culturalAreaIds: string[];
  exhibitionTypeIds: string[];
};

export type UpdateArticleTaxonomyInput = ArticleTaxonomy;

export type UpdateArticleTaxonomyResult =
  | { success: true }
  | { success: false; error: string };
